import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { loginStart, loginSuccess, loginFailure } from '../redux/slices/authSlice';
import { apiHelper } from '../utils/api';
import { API_ENDPOINTS, MIVENT_TUTORIAL_URL } from '../utils/constants';
import { showToast } from '../utils/toast';
import { handleGoogleAuth } from '../utils/googleAuth';
import { Mail, Lock, LogIn } from 'lucide-react';
import Modal from '../components/Modal/Modal';
import TermsModal from '../components/TandC/TermsModal';
import SessionManagerModal from '../components/SessionManagerModal/SessionManagerModal';
import './Login.scss';

const buildAppUser = (user) => {
  if (!user) {
    throw new Error('Login succeeded, but the server did not return a user profile. Please try again.');
  }

  return {
    id: user._id || user.id || user.uid || '',
    fullName: user.name || user.displayName || '',
    email: user.email || '',
    avatar: user.photoURL || null,
    plan: user.plan || 'free',
    createdAt: user.createdAt,
    role: user.role || 'user',
    name: user.name || user.displayName || '',
    photoURL: user.photoURL || null,
    status: user.status || 'active',
  };
};

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [termsPromptVisible, setTermsPromptVisible] = useState(false);
  const [deactivatedError, setDeactivatedError] = useState(null);
  const [credentialsError, setCredentialsError] = useState(null);
  const [sessionLimitError, setSessionLimitError] = useState(null);
  const [sessionLimitModalOpen, setSessionLimitModalOpen] = useState(false);
  const [sessionModal, setSessionModal] = useState({ open: false, sessions: [], email: '', password: '', cleanupToken: null });
  // Pending-verification full-screen view (shown when user needs to verify email)
  const [pendingVerification, setPendingVerification] = useState(
    !!searchParams.get('resend'),
  );
  const [pendingEmail, setPendingEmail] = useState(
    searchParams.get('resend') || '',
  );
  const [resendLoading, setResendLoading] = useState(false);
  const [signupOffer, setSignupOffer] = useState(null);

  // Same limited-offer banner as Register page. Silent fail — never blocks login.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiHelper.get('/plans/signup-offer');
        if (!cancelled && data?.offer) setSignupOffer(data.offer);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);
  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const termsAccepted = watch('termsAccepted', false);

  const requireTerms = (event) => {
    if (!termsAccepted && !isLoading) {
      event.preventDefault();
      setTermsPromptVisible(true);
    }
  };

  // Whether the user was redirected here after clicking the verification link
  const justVerified = searchParams.get('verified') === 'true';

  const togglePassword = () => setShowPassword((s) => !s);

  const onSubmit = async (data) => {
    if (!data.termsAccepted) {
      setTermsPromptVisible(true);
      return;
    }
    setIsLoading(true);
    setDeactivatedError(null);
    setCredentialsError(null);
    setSessionLimitError(null);
    setSessionLimitModalOpen(false);
    setSessionModal({ open: false, sessions: [], email: '', password: '' });
    setPendingVerification(false);
    setPendingEmail('');
    dispatch(loginStart());

    try {
      // The backend validates the password with bcrypt and checks emailVerified.
      const result = await apiHelper.post(API_ENDPOINTS.LOGIN, {
        email: data.email,
        password: data.password,
      });

      const { token, user } = result;

      const appUser = buildAppUser(user);

      dispatch(loginSuccess({ user: appUser, token }));
      showToast('Login successful!');
      navigate('/dashboard');

    } catch (err) {
      const errorCode = err.response?.data?.code;
      const message =
        err.response?.data?.message ||
        err.message ||
        'Login failed. Please check your credentials.';

      if (errorCode === 'ACCOUNT_DEACTIVATED') {
        setDeactivatedError(message);
        dispatch(loginFailure(message));
      } else if (errorCode === 'EMAIL_NOT_FOUND') {
        setCredentialsError({ message, remaining: null });
        dispatch(loginFailure(message));
      } else if (errorCode === 'INVALID_CREDENTIALS') {
        const remaining = err.response?.data?.remainingAttempts;
        setCredentialsError({ message, remaining });
        dispatch(loginFailure(message));
      } else if (errorCode === 'ACCOUNT_LOCKED') {
        setCredentialsError({ message, remaining: 0 });
        dispatch(loginFailure(message));
      } else if (errorCode === 'EMAIL_NOT_VERIFIED') {
        setPendingEmail(data.email);
        setPendingVerification(true);
      } else if (errorCode === 'SESSION_LIMIT_REACHED') {
        setSessionLimitError(message);
        setSessionLimitModalOpen(true);
        dispatch(loginFailure(message));
        const sessions = err.response?.data?.sessions || [];
        setSessionModal({ open: false, sessions, email: data.email, password: data.password, cleanupToken: null });
      } else {
        dispatch(loginFailure(message));
        showToast(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // From the pending-verification screen, let the user request a new link.
  const handleResendFromPending = async () => {
    setResendLoading(true);
    try {
      await apiHelper.post(API_ENDPOINTS.RESEND_VERIFICATION, { email: pendingEmail });
      showToast('Verification email resent! Please check your inbox.');
    } catch (err) {
      const message = err.response?.data?.message || 'Could not resend. Please try again.';
      showToast(message);
    } finally {
      setResendLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setDeactivatedError(null);
    setSessionLimitError(null);
    setSessionLimitModalOpen(false);
    dispatch(loginStart());
    try {
      const result = await handleGoogleAuth();
      const { token, user } = result;

      const appUser = buildAppUser(user);

      dispatch(loginSuccess({ user: appUser, token }));
      showToast('Google login successful!');
      navigate('/dashboard');
    } catch (err) {
      console.error('Google login failed', err);
      const errorCode = err.response?.data?.code;
      const message = err.response?.data?.message || err.message || 'Google login failed. Please try again.';
      
      // Check if account is deactivated
      if (errorCode === 'ACCOUNT_DEACTIVATED') {
        setDeactivatedError(message);
        dispatch(loginFailure(message));
      } else if (errorCode === 'EMAIL_NOT_VERIFIED') {
        const email = err.response?.data?.email || '';
        setPendingEmail(email);
        setPendingVerification(true);
      } else if (errorCode === 'SESSION_LIMIT_REACHED') {
        setSessionLimitError(message);
        setSessionLimitModalOpen(true);
        dispatch(loginFailure(message));
        const sessions = err.response?.data?.sessions || [];
        const cleanupToken = err.response?.data?.cleanupToken || null;
        setSessionModal({ open: false, sessions, email: '', password: '', cleanupToken });
      } else {
        dispatch(loginFailure(message));
        showToast(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── Pending verification screen ─────────────────────────────────────────────
  if (pendingVerification) {
    return (
      <div className="login">
        <div className="login__container">
          <div className="login__header">
            <div className="login__brand">
              <img src="/favi1.png" alt="MIVENT" className="login__logo-img" />
              <h1 className="login__logo">MIVENT</h1>
            </div>
            <p className="login__tagline">My Event Companion</p>
          </div>

          <div className="register__verify-pending">
            <div className="register__verify-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2>Verify your email</h2>
            <p>
              We sent a verification link to <strong>{pendingEmail}</strong>.
              Please click the link in that email to activate your account.
            </p>
            <p className="register__verify-note">
              <strong>Important:</strong> Didn&apos;t receive the email? Please check your spam or junk folder. 
              If you still don&apos;t see it, you can request a new link below.
            </p>

            <button
              type="button"
              className="login__submit"
              onClick={handleResendFromPending}
              disabled={resendLoading}
            >
              {resendLoading ? 'Sending…' : 'Resend verification email'}
            </button>

            <div className="login__footer" style={{ marginTop: '1.5rem' }}>
              <p>
                Already verified?{' '}
                <button
                  type="button"
                  className="login__link-btn"
                  onClick={() => { setPendingVerification(false); setPendingEmail(''); }}
                >
                  Sign In
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login">
      <div className="login__container">
        <div className="login__header">
          <div className="login__brand">
            <img src="/favi1.png" alt="MIVENT" className="login__logo-img" />
            <h1 className="login__logo">MIVENT</h1>
          </div>
          <p className="login__tagline">My Event Companion</p>
          <p className="login__subtitle">Welcome back! Please login to your account.</p>
          {signupOffer && (
            <div style={{
              background: signupOffer.isScheduled
                ? 'linear-gradient(135deg,#6366f1,#8b5cf6)'
                : 'linear-gradient(135deg,#f59e0b,#ef4444)',
              color: '#fff', borderRadius: 10, padding: '10px 14px',
              marginTop: 10, fontSize: 13, fontWeight: 600, textAlign: 'center',
            }}>
              {signupOffer.isScheduled ? '◷ ' : '🎁 '}{signupOffer.title || `First ${signupOffer.maxUsers} get ${String(signupOffer.plan).toUpperCase()} FREE`}
              <div style={{ fontWeight: 400, fontSize: 12, opacity: 0.95 }}>
                {signupOffer.isScheduled
                  ? `Starts ${signupOffer.startAt ? new Date(signupOffer.startAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'soon'}${signupOffer.validTill ? ` • ends ${new Date(signupOffer.validTill).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}`
                  : `${signupOffer.seatsLeft ?? '-'} seats left${signupOffer.validTill ? ` • ends ${new Date(signupOffer.validTill).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}${signupOffer.planValidTill ? ` • free till ${new Date(signupOffer.planValidTill).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}` : signupOffer.days ? ` • ${signupOffer.days} days free` : ''}`}
              </div>
            </div>
          )}
          <a
            className="login__tutorial-link"
            href="/support#tutorial"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Watch the MIVENT tutorial video"
          >
            What is MIVENT?
          </a>
        </div>

        {/* Post-verification success banner (Firebase redirected user here) */}
        {justVerified && (
          <div className="login__verified-banner">
            <span className="login__verified-icon">✓</span>
            <span>Email verified! You can now sign in.</span>
          </div>
        )}

        {/* Deactivated Account Warning */}
        {deactivatedError && (
          <div className="login__deactivated-warning">
            <div className="login__deactivated-icon">⚠️</div>
            <div className="login__deactivated-content">
              <h3>Account Terminated</h3>
              <p>{deactivatedError}</p>
            </div>
          </div>
        )}

        {/* Session Manager Modal */}
        <SessionManagerModal
          isOpen={sessionModal.open}
          onClose={() => setSessionModal((s) => ({ ...s, open: false }))}
          sessions={sessionModal.sessions}
          email={sessionModal.email}
          password={sessionModal.password}
          cleanupToken={sessionModal.cleanupToken}
          onSessionRemoved={(id) =>
            setSessionModal((s) => ({
              ...s,
              sessions: s.sessions.filter((sess) => sess.id !== id),
            }))
          }
        />

        <Modal
          isOpen={sessionLimitModalOpen && !!sessionLimitError}
          onClose={() => setSessionLimitModalOpen(false)}
          title="Session Limit Reached"
          size="small"
        >
          <div className="login__session-limit-warning login__session-limit-warning--modal">
            <div className="login__session-limit-icon">🖥️</div>
            <div className="login__session-limit-content">
              <p>{sessionLimitError}</p>
              <div className="login__session-limit-actions">
                <button
                  type="button"
                  className="login__resend-btn"
                  onClick={() => {
                    setSessionLimitModalOpen(false);
                    setSessionModal((s) => ({ ...s, open: true }));
                  }}
                >
                  Manage Active Sessions
                </button>
                <button
                  type="button"
                  className="login__link-btn"
                  onClick={() => setSessionLimitModalOpen(false)}
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </Modal>

        <form className="login__form" onSubmit={handleSubmit(onSubmit)}>
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <div className={`input-with-icon input-with-icon--leading${credentialsError ? ' input-with-icon--error' : ''}`}>
              <Mail size={18} className="input-icon input-icon--leading" />
              <input
                id="email"
                type="email"
                placeholder="Enter your email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })}
              />
            </div>
            {errors.email && <span className="error">{errors.email.message}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className={`input-with-icon${credentialsError ? ' input-with-icon--error' : ''}`}>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                {...register('password', { 
                  required: 'Password is required',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters'
                  }
                })}
              />
              <button
                type="button"
                className="toggle-visibility"
                onClick={togglePassword}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  // Eye with slash (hide)
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 3L21 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10.58 10.58a3 3 0 004.24 4.24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2.9 12.05C4.9 7.5 8.82 5 12 5c1.4 0 2.73.38 3.88 1.07" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14.12 15.88C12.98 16.5 11.52 16.5 10.38 15.88" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  // Eye (show)
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            </div>
            {errors.password && <span className="error">{errors.password.message}</span>}
          </div>

          {/* <div className="login__options">
            <label className="checkbox">
              <input type="checkbox" {...register('remember')} />
              <span>Remember me</span>
            </label>
          </div> */}

          {/* Invalid credentials / lockout error */}
          {credentialsError && (
            <div className="login__credentials-error">
              <div className="login__credentials-error-header">
                <span className="login__credentials-error-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <line x1="12" y1="8" x2="12" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <circle cx="12" cy="16.5" r="1" fill="currentColor"/>
                  </svg>
                </span>
                <span className="login__credentials-error-message">{credentialsError.message}</span>
                <button
                  type="button"
                  className="login__credentials-error-close"
                  onClick={() => setCredentialsError(null)}
                  aria-label="Dismiss error"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
              {credentialsError.remaining != null && credentialsError.remaining > 0 && (
                <span className="login__credentials-error-attempts">
                  {credentialsError.remaining} attempt{credentialsError.remaining !== 1 ? 's' : ''} remaining
                </span>
              )}
            </div>
          )}

          <div className="login__terms-toggle">
            <label className="switch">
              <input
                type="checkbox"
                {...register('termsAccepted')}
              />
              <span className="slider" />
            </label>
            <span className="login__terms-text">I agree to the <button type="button" className="login__link-btn" onClick={() => setShowTerms(true)}>Terms & Conditions</button></span>
          </div>
          {termsPromptVisible && !termsAccepted && (
            <div className="login__terms-error" role="alert">
              Please agree to the Terms &amp; Conditions before continuing.
            </div>
          )}

          <button
            type="submit"
            className={`login__submit${!termsAccepted ? ' login__submit--terms-required' : ''}`}
            disabled={isLoading}
            aria-disabled={!termsAccepted}
            onClick={requireTerms}
          >
            {isLoading ? 'Signing in...' : <><LogIn size={18} /> Sign In</>}
          </button>

          <div className="login__divider">
            <span>or</span>
          </div>

          <button 
            type="button" 
            className={`login__google-btn${!termsAccepted ? ' login__google-btn--terms-required' : ''}`}
            onClick={(event) => {
              requireTerms(event);
              if (termsAccepted) handleGoogleSignIn();
            }}
            disabled={isLoading}
            aria-disabled={!termsAccepted}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {isLoading ? 'Signing in...' : 'Sign in with Google'}
          </button>

        </form>

        <div className="login__footer">
          <div className="login__footer-links" role="navigation" aria-label="Auth links">
            <Link to="/forgot-password" className="login__link">Forgot Password</Link>
            <Link to="/register" className="login__link">Sign Up</Link>
          </div>
        </div>
      </div>
      <TermsModal open={showTerms} onClose={() => setShowTerms(false)} />
    </div>
  );
};



export default Login;
